import { Box, Checkbox, CheckboxProps, styled, Theme } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck } from "@fortawesome/pro-solid-svg-icons"
import { FC } from "react"
import { CHECKBOX_ATTRIBUTES } from "../../../constants"
import { CheckboxAttrProps, NeutralSetting, Variant } from "../../../types"
import { REVIEW_BADGE_ORIGIN, ReviewBadge } from "../../badges"

interface CheckboxComponentProps extends CheckboxProps {
  identifier?: string
  ariaLabel?: string
  accessibilityAttr?: { [key: string]: string }
  correct?: boolean
  variant?: Variant
  isLesson?: boolean
  isNeutralSetting?: NeutralSetting
  /** When true, render the checkbox visual only (no native input); parent owns role="checkbox". */
  presentationOnly?: boolean
}

const CheckboxComponent: FC<CheckboxComponentProps> = props => {
  const {
    inputProps = null,
    ariaLabel,
    identifier,
    accessibilityAttr,
    correct,
    variant,
    isLesson = false,
    isNeutralSetting,
    presentationOnly = false,
    ...restProps
  } = props
  const propsForInput = {
    ...(inputProps ? inputProps : CHECKBOX_ATTRIBUTES),
    ...(ariaLabel && { "aria-label": ariaLabel }),
    ...(identifier && { "aria-describedby": identifier }),
    ...accessibilityAttr
  } as CheckboxAttrProps

  // a11y: presentation-only drops the native <input> so the visual can sit inside a parent
  // role="checkbox" without tripping nested-interactive (SC 4.1.2); the parent owns the role,
  // checked state, and keyboard handling.
  const checkboxElement = presentationOnly ? (
    restProps.checked ? (
      <CheckedIcon variant={variant} isLesson={isLesson} />
    ) : (
      <UnCheckedIcon
        variant={variant}
        isLesson={isLesson}
        presentationDisabled={!!restProps.disabled}
      />
    )
  ) : (
    <StyledCheckbox
      disableRipple
      checkedIcon={<CheckedIcon variant={variant} isLesson={isLesson} />}
      icon={<UnCheckedIcon variant={variant} isLesson={isLesson} />}
      inputProps={propsForInput}
      {...restProps}
    />
  )

  if (correct !== undefined) {
    return (
      <ReviewBadge
        correct={correct}
        anchorOrigin={
          isLesson
            ? REVIEW_BADGE_ORIGIN.topLeft
            : REVIEW_BADGE_ORIGIN.bottomRight
        }
        badgeBorder={isLesson}
        isNeutralSetting={isNeutralSetting}
      >
        {checkboxElement}
      </ReviewBadge>
    )
  }

  return <>{checkboxElement}</>
}

const disabledUncheckedStyle = (theme: Theme) => ({
  backgroundColor: "transparent",
  borderColor: `${theme.palette.grey[100]} !important`
})

const UnCheckedIcon = styled(Box, {
  shouldForwardProp: prop => prop !== "presentationDisabled"
})<{ variant: Variant; isLesson: boolean; presentationDisabled?: boolean }>(
  ({ theme, variant, isLesson, presentationDisabled }) => ({
    width: "30px",
    height: "30px",
    backgroundColor: "white",
    border: `2px solid ${theme.palette.blue[100]}`,
    borderRadius: `${theme.shape.borderRadius}px`,
    transition: theme.transitions.create(["background-color", "border-color"]),

    "input:disabled ~ &": disabledUncheckedStyle(theme),
    "input:disabled:checked ~ &": {
      ...(isLesson
        ? {
            borderColor:
              variant === Variant.neutral
                ? theme.palette.blue["A800"]
                : variant === Variant.correct
                ? theme.palette.lightGreen[700]
                : theme.palette.orange[700],
            backgroundColor:
              variant === Variant.neutral
                ? theme.palette.blue["A800"]
                : variant === Variant.correct
                ? theme.palette.lightGreen[700]
                : theme.palette.orange[700]
          }
        : {
            backgroundColor: theme.palette.grey[400],
            borderColor: theme.palette.grey[100]
          })
    },

    // a11y: no <input> sibling in presentation mode, so drive the disabled visual by prop.
    ...(presentationDisabled ? disabledUncheckedStyle(theme) : {}),

    [theme.breakpoints.down("mobile")]: {
      width: "25px",
      height: "25px"
    }
  })
)

const CheckedIcon = ({
  variant,
  isLesson
}: {
  variant?: Variant
  isLesson?: boolean
}) => {
  return (
    <CheckedIconWrapper variant={variant} isLesson={isLesson}>
      <FontAwesomeIcon icon={faCheck} />
    </CheckedIconWrapper>
  )
}

const CheckedIconWrapper = styled(UnCheckedIcon)<{
  variant: Variant
  isLesson: boolean
}>(({ theme, variant, isLesson }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: theme.palette.grey[700],
  borderColor: theme.palette.grey[700],
  transition: theme.transitions.create([
    "background-color",
    "border-color",
    "fill"
  ]),

  "& > svg": {
    ...(isLesson && variant
      ? {
          fill:
            variant === Variant.correct
              ? theme.palette.lightGreen[700]
              : theme.palette.orange[700]
        }
      : {
          fill: theme.palette.background.paper
        }),
    color: theme.palette.background.paper
  },

  ...(variant &&
    !isLesson && {
      borderColor:
        variant === Variant.neutral
          ? theme.palette.blue[300]
          : variant === Variant.correct
          ? theme.palette.lightGreen[300]
          : theme.palette.orange[300],
      backgroundColor: theme.palette.background.paper,

      "& > svg": {
        fill:
          variant === Variant.neutral
            ? theme.palette.blue["A800"]
            : variant === Variant.correct
            ? theme.palette.lightGreen[700]
            : theme.palette.orange[700],
        color:
          variant === Variant.neutral
            ? theme.palette.blue["A800"]
            : variant === Variant.correct
            ? theme.palette.lightGreen[700]
            : theme.palette.orange[700]
      }
    }),

  ...(variant &&
    isLesson && {
      borderColor:
        variant === Variant.neutral
          ? theme.palette.blue["A800"]
          : variant === Variant.correct
          ? theme.palette.lightGreen[700]
          : theme.palette.orange[700],
      backgroundColor:
        variant === Variant.neutral
          ? theme.palette.blue["A800"]
          : variant === Variant.correct
          ? theme.palette.lightGreen[700]
          : theme.palette.orange[700]
    })
}))

const StyledCheckbox = styled(Checkbox)`
  padding: 0;
`

export default CheckboxComponent
